import { useState, useEffect, useCallback } from 'react';
import tmdbService from '../services/tmdbApi';

// Custom hook for TMDB API operations
export const useTMDB = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Generic function to handle API calls with loading states
  const apiCall = useCallback(async (apiFunction, ...args) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiFunction.apply(tmdbService, args);
      return result;
    } catch (err) {
      setError(err.message || 'Something went wrong');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    apiCall,
    // Convenience methods
    searchMulti: useCallback((query, options) => apiCall(tmdbService.searchMulti, query, options), [apiCall]),
    searchMovies: useCallback((query, options) => apiCall(tmdbService.searchMovies, query, options), [apiCall]),
    searchTVShows: useCallback((query, options) => apiCall(tmdbService.searchTVShows, query, options), [apiCall]),
    searchPeople: useCallback((query, options) => apiCall(tmdbService.searchPeople, query, options), [apiCall]),
    getTrending: useCallback((timeWindow, options) => apiCall(tmdbService.getTrending, timeWindow, options), [apiCall]),
    getPopularMovies: useCallback((options) => apiCall(tmdbService.getPopularMovies, options), [apiCall]),
    getPopularTVShows: useCallback((options) => apiCall(tmdbService.getPopularTVShows, options), [apiCall]),
    getTopRatedMovies: useCallback((options) => apiCall(tmdbService.getTopRatedMovies, options), [apiCall]),
    getTopRatedTVShows: useCallback((options) => apiCall(tmdbService.getTopRatedTVShows, options), [apiCall]),
    getUpcomingMovies: useCallback((options) => apiCall(tmdbService.getUpcomingMovies, options), [apiCall]),
    getNowPlayingMovies: useCallback((options) => apiCall(tmdbService.getNowPlayingMovies, options), [apiCall]),
    getMovieDetails: useCallback((movieId) => apiCall(tmdbService.getMovieDetails, movieId), [apiCall]),
    getTVShowDetails: useCallback((tvId) => apiCall(tmdbService.getTVShowDetails, tvId), [apiCall]),
    getMovieGenres: useCallback(() => apiCall(tmdbService.getMovieGenres), [apiCall]),
    getTVGenres: useCallback(() => apiCall(tmdbService.getTVGenres), [apiCall]),
    getMoviesByGenre: useCallback((genreId, options) => apiCall(tmdbService.getMoviesByGenre, genreId, options), [apiCall]),
    getTVShowsByGenre: useCallback((genreId, options) => apiCall(tmdbService.getTVShowsByGenre, genreId, options), [apiCall])
  };
};

// Hook for search functionality with debouncing
export const useTMDBSearch = (query, options = {}) => {
  const [results, setResults] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearching, setIsSearching] = useState(false);
  const { apiCall } = useTMDB();

  const debounceMs = options.debounceMs || 500;

  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (query && query.trim().length > 0 && !isSearching) {
        setIsSearching(true);
        try {
          const response = await apiCall(tmdbService.searchMulti, query, { 
            ...options, 
            page: currentPage 
          });
          if (response && response.results) {
            setResults(response.results);
            setTotalPages(response.total_pages);
            setTotalResults(response.total_results);
          }
        } catch (err) {
          console.error('Search error:', err);
          setResults([]);
          setTotalPages(0);
          setTotalResults(0);
        } finally {
          setIsSearching(false);
        }
      } else if (!query || query.trim().length === 0) {
        setResults([]);
        setTotalPages(0);
        setTotalResults(0);
        setCurrentPage(1);
      }
    }, debounceMs);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [query]); // Only depend on query to prevent multiple calls

  const refetch = useCallback(() => {
    if (query && query.trim().length > 0) {
      setIsSearching(true);
      return apiCall(tmdbService.searchMulti, query, { 
        ...options, 
        page: currentPage 
      });
    }
  }, [query, options, currentPage, apiCall]);

  return {
    results,
    loading: isSearching,
    error: null,
    totalPages,
    totalResults,
    currentPage,
    setCurrentPage,
    refetch
  };
};

// Hook for getting trending content
export const useTMDBTrending = (timeWindow = 'week', options = {}) => {
  const [results, setResults] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(options.page || 1);
  const { loading, error, getTrending } = useTMDB();

  useEffect(() => {
    const fetchTrending = async () => {
      try {
        const response = await getTrending(timeWindow, { ...options, page: currentPage });
        setResults(response.results);
        setTotalPages(response.total_pages);
        setTotalResults(response.total_results);
      } catch (err) {
        setResults([]);
        setTotalPages(0);
        setTotalResults(0);
      }
    };

    fetchTrending();
  }, [timeWindow, currentPage, options, getTrending]);

  return {
    results,
    loading,
    error,
    totalPages,
    totalResults,
    currentPage,
    setCurrentPage,
    refetch: () => getTrending(timeWindow, { ...options, page: currentPage })
  };
};

// Hook for getting popular content
export const useTMDBPopular = (type = 'movies', options = {}) => {
  const [results, setResults] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(options.page || 1);
  const { loading, error, getPopularMovies, getPopularTVShows } = useTMDB();

  useEffect(() => {
    const fetchPopular = async () => {
      try {
        const response = type === 'movies' 
          ? await getPopularMovies({ ...options, page: currentPage })
          : await getPopularTVShows({ ...options, page: currentPage });
        
        setResults(response.results);
        setTotalPages(response.total_pages);
        setTotalResults(response.total_results);
      } catch (err) {
        setResults([]);
        setTotalPages(0);
        setTotalResults(0);
      }
    };

    fetchPopular();
  }, [type, currentPage, options, getPopularMovies, getPopularTVShows]);

  return {
    results,
    loading,
    error,
    totalPages,
    totalResults,
    currentPage,
    setCurrentPage,
    refetch: () => type === 'movies' 
      ? getPopularMovies({ ...options, page: currentPage })
      : getPopularTVShows({ ...options, page: currentPage })
  };
};

// Hook for getting content by genre
export const useTMDBByGenre = (genreId, type = 'movies', options = {}) => {
  const [results, setResults] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(options.page || 1);
  const { loading, error, getMoviesByGenre, getTVShowsByGenre } = useTMDB();

  useEffect(() => {
    const fetchByGenre = async () => {
      try {
        const response = type === 'movies' 
          ? await getMoviesByGenre(genreId, { ...options, page: currentPage })
          : await getTVShowsByGenre(genreId, { ...options, page: currentPage });
        
        setResults(response.results);
        setTotalPages(response.total_pages);
        setTotalResults(response.total_results);
      } catch (err) {
        setResults([]);
        setTotalPages(0);
        setTotalResults(0);
      }
    };

    if (genreId) {
      fetchByGenre();
    }
  }, [genreId, type, currentPage, options, getMoviesByGenre, getTVShowsByGenre]);

  return {
    results,
    loading,
    error,
    totalPages,
    totalResults,
    currentPage,
    setCurrentPage,
    refetch: () => type === 'movies' 
      ? getMoviesByGenre(genreId, { ...options, page: currentPage })
      : getTVShowsByGenre(genreId, { ...options, page: currentPage })
  };
};

// Hook for getting movie/TV details
export const useTMDBDetails = (id, type = 'movie') => {
  const [details, setDetails] = useState(null);
  const { loading, error, getMovieDetails, getTVShowDetails } = useTMDB();

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const response = type === 'movie' 
          ? await getMovieDetails(id)
          : await getTVShowDetails(id);
        
        setDetails(response);
      } catch (err) {
        setDetails(null);
      }
    };

    if (id) {
      fetchDetails();
    }
  }, [id, type, getMovieDetails, getTVShowDetails]);

  return {
    details,
    loading,
    error,
    refetch: () => type === 'movie' ? getMovieDetails(id) : getTVShowDetails(id)
  };
};

export default useTMDB;
